// src/hooks/useTrendState.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTrendAnalysis } from './useTrendAnalysis';
import { useTrendData } from './useTrendData';

export const useTrendState = ({
  filterData,
  tabKey,
  shouldFetch,
  cacheMaxAge = 5 * 60 * 1000,
  minLoadingTime = 500
}) => {
  const [error, setError] = useState(null);
  const [showLoading, setShowLoading] = useState(false);
  const [retryTrigger, setRetryTrigger] = useState(0);
  const cache = useRef(new Map());
  const loadingTimer = useRef(null);

  const {
    data: analysisData,
    loading: analysisLoading,
    error: analysisError
  } = useTrendAnalysis(
    filterData.categoryId,
    filterData.mainFilterGroupIds,
    filterData.comparisonFilterGroupIds,
    shouldFetch,
    retryTrigger
  );

  const {
    data: trendData,
    loading: trendLoading,
    error: trendError
  } = useTrendData(analysisData, tabKey, shouldFetch && !!analysisData, retryTrigger);

  const cacheKey = `${tabKey}-${JSON.stringify(filterData)}`;

  const getCachedData = useCallback(() => {
    const cached = cache.current.get(cacheKey);
    if (!cached || Date.now() > cached.expiresAt) {
      cache.current.delete(cacheKey);
      return null;
    }
    return cached.data;
  }, [cacheKey]);

  useEffect(() => {
    const isLoading = analysisLoading || trendLoading;
    if (isLoading) {
      setShowLoading(true);
    } else {
      loadingTimer.current = setTimeout(() => {
        setShowLoading(false);
      }, minLoadingTime);
    }

    return () => {
      if (loadingTimer.current) clearTimeout(loadingTimer.current);
    };
  }, [analysisLoading, trendLoading, minLoadingTime]);

  useEffect(() => {
    const currentError = analysisError || trendError;
    if (currentError) {
      console.error('Trend error:', currentError);
      setError(currentError);
    } else {
      setError(null);
    }
  }, [analysisError, trendError]);

  useEffect(() => {
    if (trendData) {
      cache.current.set(cacheKey, {
        data: trendData,
        timestamp: Date.now(),
        expiresAt: Date.now() + cacheMaxAge
      });
    }
  }, [trendData, cacheKey, cacheMaxAge]);

  // Enhanced retry function that actually refetches data
  const handleRetry = useCallback(() => {
    // Clear all caches
    cache.current.clear();
    // Reset error state
    setError(null);
    // Increment retry trigger to force refetch
    setRetryTrigger(prev => prev + 1);
  }, []);

  return {
    data: getCachedData() || trendData,
    isLoading: showLoading,
    error,
    retry: handleRetry,
    resetError: useCallback(() => setError(null), []),
    clearCache: useCallback(() => cache.current.clear(), [])
  };
};